/**
 * A render tick that fires the moment a known deadline passes (a kickoff), so a memo that reads the
 * clock recomputes exactly then instead of waiting for the next refetch. React Query's structural
 * sharing hands back the same data object when nothing changed server-side, so a refetch alone would
 * not re-run such a memo (specs/home-page AC-37).
 */

import { useEffect, useState } from 'react';

/** setTimeout's ceiling; a deadline further out is re-armed on the next render anyway. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;
/** Small grace so the server has crossed the boundary too. */
const GRACE_MS = 1000;

/**
 * @returns a counter that increments once each time `deadlineIso` passes; put it in the memo's inputs.
 */
export function useTickAt(deadlineIso: string | null | undefined): number {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!deadlineIso) return;
    const deadline = new Date(deadlineIso).getTime();
    if (Number.isNaN(deadline)) return;
    const delay = Math.min(Math.max(deadline - Date.now() + GRACE_MS, 0), MAX_TIMEOUT_MS);
    const timer = setTimeout(() => setTick((t) => t + 1), delay);
    return () => clearTimeout(timer);
  }, [deadlineIso]);

  return tick;
}
